import { createServerFn } from "@tanstack/react-start";

import {
  specCreateBaseFromCoreInputSchema,
  specCreateCommunityFromBaseInputSchema,
  specCreateInputSchema,
  specCreateVersionInputSchema,
  specDeleteVersionInputSchema,
  specDuplicateInputSchema,
  specDistinctValuesInputSchema,
  specLinesInputSchema,
  specLinkedCommunitiesInputSchema,
  specPushPreviewInputSchema,
  specPushProgressInputSchema,
  specPushStartInputSchema,
  specReviewLinesInputSchema,
  specListQueryInputSchema,
  specPublishInputSchema,
  specRenameInputSchema,
  specSetArchivedInputSchema,
  specSummaryByVersionInputSchema,
  specUnlinkCoreInputSchema,
  specSummaryInputSchema,
  type SpecCreateBaseFromCoreBrowserInput,
  type SpecCreateBrowserInput,
  type SpecCreateCommunityFromBaseBrowserInput,
  type SpecCreateVersionBrowserInput,
  type SpecDeleteVersionBrowserInput,
  type SpecDuplicateBrowserInput,
  type SpecDistinctValuesBrowserInput,
  type SpecLinesBrowserInput,
  type SpecLinkedCommunitiesBrowserInput,
  type SpecPushPreviewBrowserInput,
  type SpecPushProgressBrowserInput,
  type SpecPushStartBrowserInput,
  type SpecReviewLinesBrowserInput,
  type SpecListQueryBrowserInput,
  type SpecPublishBrowserInput,
  type SpecRenameBrowserInput,
  type SpecSetArchivedBrowserInput,
  type SpecSummaryBrowserInput,
  type SpecSummaryByVersionBrowserInput,
  type SpecUnlinkCoreBrowserInput,
} from "@/api/contracts/specifications";
import { assertCanAdministrate, assertCanEditSpecs } from "@/api/functions/admin-gate";
import { requireAuth } from "@/api/middleware/auth";
import { BffUserFacingError } from "@/lib/errors";
import { derivePermissions } from "@/lib/roles";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";

// The browser's doors onto the spec reads: the Spec list's page of a tab and one
// filterable column's distinct values (#689); the details page's spec summary,
// by spec id and by version id, and a version's lines page (#693). The three
// details reads answer `null` for a declared absence — a dead or foreign id —
// so the route renders Page not found or the base list rather than an error.
// And onto the four lifecycle verbs (#707): create, rename, set archived and
// delete version, each behind its own capability check below — Unlink core
// (#1085), the Admin console's, behind the Admin gate — and the version
// cloner's (#804): Create Version, Duplicate, and a base from a core or a
// community from a base, the two creates one wire with the type fixed here.
//
// Three gates stand between a browser and a spec row, the catalog's three
// (`@/api/functions/catalog`):
//   1. `requireAuth` — no session, no call.
//   2. `assertCanBrowseSpecs` below — the session's roles must grant the
//      capability. It stops an authenticated identity with no Spec Tool tier
//      (the trade partner, the migrator, the system actor) before the round
//      trip, and it runs here rather than in a route guard because a server
//      function is callable without ever rendering a route. It sits in the
//      handler rather than a middleware so a plain unit test reaches it.
//   3. Python's `require_any_role(SPEC_READ_ROLES)` and the use cases' own
//      assertion behind it — the enforcement. The BFF gate is defense in depth
//      and a better error, pinned to its Python counterpart by
//      `@/lib/spec-read-access-drift.unit.test.ts`.
//
// Error scrubbing is global function middleware (`start.ts`). Server-only
// modules are pulled in with a dynamic `import()` inside the handler.

interface HandlerContext {
  session: ResolvedSession;
}

/**
 * The capability, not a role name. The tag is the bare `role-forbidden`, which
 * is on the user-facing allowlist, so the message reaches the browser intact.
 */
export function assertCanBrowseSpecs(session: ResolvedSession): void {
  if (derivePermissions(session.roles).canBrowseSpecs) return;
  throw new BffUserFacingError(
    "role-forbidden",
    "You don't have permission to browse specifications.",
    403,
  );
}

/**
 * The write gates, the read gate's siblings: `canEditSpecs` (`assertCanEditSpecs`,
 * shared with the Editor's verbs from the admin gate) mirrors Python's
 * `SPEC_WRITE_ROLES` and `canCreateCoreSpecs` its `SPEC_CORE_CREATE_ROLES`
 * (`@/lib/spec-write-access-drift.unit.test.ts`). The Division Editor's scope
 * is Python's alone — it needs the spec's division and the Editable divisions.
 */
function assertCanCreateCoreSpecs(session: ResolvedSession): void {
  if (derivePermissions(session.roles).canCreateCoreSpecs) return;
  throw new BffUserFacingError(
    "role-forbidden",
    "You don't have permission to create a core specification.",
    403,
  );
}

async function handleList({
  data,
  context,
}: {
  data: SpecListQueryBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { listInternalSpecifications } = await import("@/server/data/internal-api/specifications");
  // Straight through: the declaration's encoder owns which keys reach the wire.
  return listInternalSpecifications(data, { sessionId: context.session.sessionId });
}

async function handleDistinctValues({
  data,
  context,
}: {
  data: SpecDistinctValuesBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { listInternalSpecificationDistinctValues } =
    await import("@/server/data/internal-api/specifications");
  return listInternalSpecificationDistinctValues(data, {
    sessionId: context.session.sessionId,
  });
}

async function handleSummary({
  data,
  context,
}: {
  data: SpecSummaryBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { getInternalSpecificationSummary } =
    await import("@/server/data/internal-api/specifications");
  return getInternalSpecificationSummary(data, { sessionId: context.session.sessionId });
}

async function handleSummaryByVersion({
  data,
  context,
}: {
  data: SpecSummaryByVersionBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { getInternalSpecificationSummaryByVersion } =
    await import("@/server/data/internal-api/specifications");
  return getInternalSpecificationSummaryByVersion(data, {
    sessionId: context.session.sessionId,
  });
}

async function handleLines({
  data,
  context,
}: {
  data: SpecLinesBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { listInternalSpecificationVersionLines } =
    await import("@/server/data/internal-api/specifications");
  return listInternalSpecificationVersionLines(data, { sessionId: context.session.sessionId });
}

async function handleReviewLines({
  data,
  context,
}: {
  data: SpecReviewLinesBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { listInternalSpecificationVersionReviewLines } =
    await import("@/server/data/internal-api/specifications");
  return listInternalSpecificationVersionReviewLines(data, {
    sessionId: context.session.sessionId,
  });
}

async function handleCreate({
  data,
  context,
}: {
  data: SpecCreateBrowserInput;
  context: HandlerContext;
}) {
  if (data.type === "core") assertCanCreateCoreSpecs(context.session);
  else assertCanEditSpecs(context.session);
  const { createInternalSpecification } = await import("@/server/data/internal-api/specifications");
  return createInternalSpecification(data, { sessionId: context.session.sessionId });
}

/** A list verb's browser input as its internal face names the spec: by
 *  `specificationId`, the path segment, rather than the page's `specId`. */
function bySpecificationId<T extends { specId: number }>({ specId, ...rest }: T) {
  return { ...rest, specificationId: specId };
}

async function handleRename({
  data,
  context,
}: {
  data: SpecRenameBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { renameInternalSpecification } = await import("@/server/data/internal-api/specifications");
  return renameInternalSpecification(bySpecificationId(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleSetArchived({
  data,
  context,
}: {
  data: SpecSetArchivedBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { setInternalSpecificationArchived } =
    await import("@/server/data/internal-api/specifications");
  return setInternalSpecificationArchived(bySpecificationId(data), {
    sessionId: context.session.sessionId,
  });
}

/**
 * Unlink core (#1085): an Admin's alone (ADR-0011), so the gate is the
 * Admin console's, not the editors' — Python's use case is still the one that
 * counts.
 */
async function handleUnlinkCore({
  data,
  context,
}: {
  data: SpecUnlinkCoreBrowserInput;
  context: HandlerContext;
}) {
  assertCanAdministrate(context.session, "core spec links");
  const { unlinkInternalSpecificationCore } =
    await import("@/server/data/internal-api/specifications");
  return unlinkInternalSpecificationCore(bySpecificationId(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleDeleteVersion({
  data,
  context,
}: {
  data: SpecDeleteVersionBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { deleteInternalSpecificationVersion } =
    await import("@/server/data/internal-api/specifications");
  return deleteInternalSpecificationVersion(bySpecificationId(data), {
    sessionId: context.session.sessionId,
  });
}

// --- the version cloner (#804) ------------------------------------------------
//
// Every cloner verb takes the edit gate alone. A core Duplicate is a core write,
// but the browser input does not carry the source's type, so that refusal is
// Python's (`role-forbidden:spec:create-core`), as the Division Editor's scope
// is.

async function handleCreateVersion({
  data,
  context,
}: {
  data: SpecCreateVersionBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { createInternalSpecVersion } = await import("@/server/data/internal-api/spec-editor");
  return createInternalSpecVersion(
    { specificationId: data.specId },
    { sessionId: context.session.sessionId },
  );
}

async function handleDuplicate({
  data,
  context,
}: {
  data: SpecDuplicateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { duplicateInternalSpecification } = await import("@/server/data/internal-api/spec-editor");
  return duplicateInternalSpecification(bySpecificationId(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleCreateBaseFromCore({
  data,
  context,
}: {
  data: SpecCreateBaseFromCoreBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { createInternalSpecificationFromParent } =
    await import("@/server/data/internal-api/spec-editor");
  return createInternalSpecificationFromParent(
    { type: "base", ...data },
    { sessionId: context.session.sessionId },
  );
}

async function handleCreateCommunityFromBase({
  data,
  context,
}: {
  data: SpecCreateCommunityFromBaseBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { createInternalSpecificationFromParent } =
    await import("@/server/data/internal-api/spec-editor");
  return createInternalSpecificationFromParent(
    { type: "community", ...data },
    { sessionId: context.session.sessionId },
  );
}

// --- publish (#949) -----------------------------------------------------------
//
// The edit gate, then the input forwarded as is: the Division Editor's scope,
// the guard and the code are Python's, and its two refusals reach the dialog
// by their tags.

async function handlePublishVersion({
  data,
  context,
}: {
  data: SpecPublishBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { publishInternalSpecVersion } = await import("@/server/data/internal-api/spec-editor");
  return publishInternalSpecVersion(data, { sessionId: context.session.sessionId });
}

// --- the batch push (#1088, over #1143's two routes) ----------------------------
//
// Review & Publish's step 3 lists the linked communities and starts a push;
// the poll reads its progress. Both reads are any spec reader's, as Python's
// gates are (Q8, Assumption 6); the start is an edit, gated as Publish is.

async function handleLinkedCommunities({
  data,
  context,
}: {
  data: SpecLinkedCommunitiesBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { listInternalSpecLinkedCommunities } =
    await import("@/server/data/internal-api/specifications");
  return listInternalSpecLinkedCommunities(data, { sessionId: context.session.sessionId });
}

async function handleStartPush({
  data,
  context,
}: {
  data: SpecPushStartBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { startInternalSpecPush } = await import("@/server/data/internal-api/specifications");
  return startInternalSpecPush(data, { sessionId: context.session.sessionId });
}

async function handlePushProgress({
  data,
  context,
}: {
  data: SpecPushProgressBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseSpecs(context.session);
  const { getInternalSpecPush } = await import("@/server/data/internal-api/specifications");
  return getInternalSpecPush(data, { sessionId: context.session.sessionId });
}

// The push preview (#1090): a read gated as the push it previews, an edit
// (Python's gate is the push's own write set).
async function handlePushPreview({
  data,
  context,
}: {
  data: SpecPushPreviewBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { previewInternalSpecPush } = await import("@/server/data/internal-api/specifications");
  return previewInternalSpecPush(data, { sessionId: context.session.sessionId });
}

/**
 * The handler bodies, exported for unit tests: a `createServerFn` value is not
 * callable as a plain function outside the Start runtime.
 */
export const __test_handlers = {
  handleList,
  handleDistinctValues,
  handleSummary,
  handleSummaryByVersion,
  handleLines,
  handleReviewLines,
  handleCreate,
  handleRename,
  handleSetArchived,
  handleUnlinkCore,
  handleDeleteVersion,
  handleCreateVersion,
  handleDuplicate,
  handleCreateBaseFromCore,
  handleCreateCommunityFromBase,
  handlePublishVersion,
  handleLinkedCommunities,
  handleStartPush,
  handlePushProgress,
  handlePushPreview,
};

export const specificationsList = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specListQueryInputSchema)
  .handler(handleList);

export const specificationDistinctValues = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specDistinctValuesInputSchema)
  .handler(handleDistinctValues);

export const specificationSummary = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(specSummaryInputSchema)
  .handler(handleSummary);

export const specificationSummaryByVersion = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(specSummaryByVersionInputSchema)
  .handler(handleSummaryByVersion);

export const specificationVersionLines = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLinesInputSchema)
  .handler(handleLines);

/** The Review & Publish dialog's step 1 (#950): a page of the lines still to
 *  finalize, with the version's gating. */
export const specificationVersionReviewLines = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specReviewLinesInputSchema)
  .handler(handleReviewLines);

export const specificationCreate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specCreateInputSchema)
  .handler(handleCreate);

export const specificationRename = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specRenameInputSchema)
  .handler(handleRename);

export const specificationSetArchived = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSetArchivedInputSchema)
  .handler(handleSetArchived);

export const specificationUnlinkCore = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specUnlinkCoreInputSchema)
  .handler(handleUnlinkCore);

export const specificationDeleteVersion = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specDeleteVersionInputSchema)
  .handler(handleDeleteVersion);

export const specificationCreateVersion = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specCreateVersionInputSchema)
  .handler(handleCreateVersion);

export const specificationDuplicate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specDuplicateInputSchema)
  .handler(handleDuplicate);

export const specificationCreateBaseFromCore = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specCreateBaseFromCoreInputSchema)
  .handler(handleCreateBaseFromCore);

export const specificationCreateCommunityFromBase = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specCreateCommunityFromBaseInputSchema)
  .handler(handleCreateCommunityFromBase);

export const specificationPublishVersion = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specPublishInputSchema)
  .handler(handlePublishVersion);

/** Step 3's picker (#1088): the base's linked communities for this version. */
export const specificationLinkedCommunities = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(specLinkedCommunitiesInputSchema)
  .handler(handleLinkedCommunities);

/** Start a batch push; answers `{ pushId }`. */
export const specificationStartPush = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specPushStartInputSchema)
  .handler(handleStartPush);

/** A push's progress, or `null` when no push has the id (#1143's declaration). */
export const specificationPushProgress = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(specPushProgressInputSchema)
  .handler(handlePushProgress);

/** What a push would do to one community, worked out without saving (#1090). */
export const specificationPushPreview = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specPushPreviewInputSchema)
  .handler(handlePushPreview);
